import Vue from 'vue';
import { castArray, intersection } from 'lodash-es';
import mergeFn from '@/common/utils/merge';
import Model from '@/common/models/common.model';
import { CommonState, ItemsMap } from '@/common/store/modules/common/types/CommonState';
import { CommonRequestView } from '@/common/store/modules/common/types/CommonRequestView';
import { DEFAULT_REQUEST_VIEW, addRequestView } from './common.constants';

interface CommonMutationPayload {
  view: string
}

interface RemoveFromListPayload extends CommonMutationPayload {
  id: ID
}

interface ChangeItemPayload extends CommonMutationPayload {
  item: Model,
  merge: boolean
  addToList: boolean
}

interface SetItemsPayload extends CommonMutationPayload {
  items: Model[],
  merge: boolean
  addToList: boolean
}

type ChangeItemsPayload = SetItemsPayload;

interface SetCountPayload {
  count?: number,
  view?: string
}

interface SetIsLastPagePayload extends CommonMutationPayload {
  view: string,
  isLastPage: boolean
}

interface SetMarkerPayload {
  view?: string,
  marker?: Marker
}

interface SetRequestParamsPayload extends CommonMutationPayload {
  requestParams: FetchRequestParams
}

interface SetItemsPayload extends CommonMutationPayload {
  byId: object,
  ids: ID[],
  includeId: ID[]
}

type PushItemsPayload = SetItemsPayload;

/**
 * Merge old Model into new Model or
 * merge new object into old object
 * @param oldObj
 * @param newObj
 * @returns {*}
 */
function mergeObjects(oldObj, newObj): object {
  if (
    (oldObj instanceof Model && !(newObj instanceof Model))
    || (newObj instanceof Model && !(oldObj instanceof Model))
  ) {
    throw new Error('Provide valid arguments in mergeObjects');
  }

  if (oldObj instanceof Model) {
    return newObj.merge(oldObj.toJS());
  }

  return mergeFn(oldObj, newObj);
}

export function changeItem(itemsMap: ItemsMap, item, merge = true): void {
  const oldItem = itemsMap[item.id];
  if (!oldItem) {
    Vue.set(itemsMap, item.id, item);
    return;
  }

  if (!item.hash || item.hash !== oldItem.hash) {
    itemsMap[item.id] = merge ? mergeObjects(oldItem, item) : item;
  }
}

export function takeViewList(state, view): CommonRequestView {
  let viewList = state.viewLists[view];
  if (!viewList) {
    Vue.set(state.viewLists, view, addRequestView());
    viewList = state.viewLists[view];
  }
  return viewList;
}

const createMutations = (getInitialState?): CommonMutationTree<CommonState> => ({
  ADD_REQUEST_VIEW(state, view): void {
    const viewList = state.viewLists;
    if (view && !viewList[view]) {
      Vue.set(viewList, view, addRequestView());
    }
  },

  SET(state, { items = [], merge = true, view = DEFAULT_REQUEST_VIEW }: SetItemsPayload): void {
    state.viewLists[view].list = items.map(it => it.id);
    const buffer = {};
    let changes = 0;
    items.forEach((item) => {
      if (state.itemsMap[item.id]) {
        if (!item.hash || item.hash !== state.itemsMap[item.id].hash) {
          changes++;
          buffer[item.id] = merge ? mergeObjects(state.itemsMap[item.id], item) : item;
        }
      } else {
        changes++;
        buffer[item.id] = item;
      }
    });
    if (changes) {
      state.itemsMap = {
        ...state.itemsMap,
        ...buffer
      };
    }
  },

  SET_COUNT(state, payload: SetCountPayload = {}): void {
    const { count, view = DEFAULT_REQUEST_VIEW } = payload;

    const stateItem = state.viewLists[view];
    if (typeof count === 'number') {
      stateItem.count = count;
    } else if (typeof stateItem.count === 'number') {
      stateItem.count = null;
    }
  },

  SET_IS_LAST_PAGE(state, { view = DEFAULT_REQUEST_VIEW, isLastPage }: SetIsLastPagePayload): void {
    state.viewLists[view].isLastPage = isLastPage;
  },

  SET_MARKER(state, { view = DEFAULT_REQUEST_VIEW, marker }: SetMarkerPayload): void {
    const viewList = state.viewLists[view];
    if (viewList) {
      viewList.marker = marker;
    }
  },

  SET_REQUEST_PARAMS(state, { requestParams = {}, view = DEFAULT_REQUEST_VIEW }: SetRequestParamsPayload): void {
    state.viewLists[view].requestParams = requestParams;
  },

  RESET_PAGING(state, view: string = DEFAULT_REQUEST_VIEW): void {
    const viewList = state.viewLists[view];
    if (viewList) {
      viewList.marker = undefined;
      viewList.isLastPage = false;
      viewList.requestParams = {};
    }
  },

  DELETE_VIEW({ viewLists }, view: string): void {
    if (viewLists && view && view !== DEFAULT_REQUEST_VIEW) {
      Vue.delete(viewLists, view);
    }
  },

  SET_ITEMS(state, { byId = {}, ids = [], includeId = [], view = DEFAULT_REQUEST_VIEW }: SetItemsPayload): void {
    const { itemsMap, viewLists } = state;
    const includeIds = Array.isArray(includeId) ? includeId : [includeId];
    const buffer = {};

    ids.forEach((id) => {
      const item = byId[id];
      const oldItem = itemsMap[id];

      if (!oldItem) {
        buffer[id] = item;
        return;
      }
      if (!item.hash || item.hash !== oldItem.hash) {
        buffer[id] = mergeObjects(oldItem, item);
      }
    });

    if (Object.keys(buffer).length > 0) {
      state.itemsMap = {
        ...state.itemsMap,
        ...buffer
      };
    }

    const viewList = viewLists[view];
    if (includeIds.length) {
      viewList.list = includeIds.reduce((list, id) => id && itemsMap[id] && !list.includes(id)
          ? list.concat(id)
          : list,
        ids);
      return;
    }
    viewList.list = ids;
  },

  PUSH_ITEMS(state, { byId = {}, ids = [], view = DEFAULT_REQUEST_VIEW }: PushItemsPayload): void {
    state.itemsMap = { ...state.itemsMap, ...byId };

    let viewList = state.viewLists[view];
    if (!viewList) {
      Vue.set(state.viewLists, view, addRequestView());
      viewList = state.viewLists[view];
    }

    // mpetrov: some ids may be already pushed by sockets
    viewList.list = viewList.list?.filter(id => !ids.includes(id)).concat(ids);
  },

  CHANGE(state, { item, merge = true, view = DEFAULT_REQUEST_VIEW, addToList = true }: ChangeItemPayload): void {
    const viewList = takeViewList(state, view);
    if (addToList && !viewList.list.includes(item.id)) {
      viewList.list.push(item.id);
      if (typeof viewList.count === 'number') {
        viewList.count++;
      }
    }
    changeItem(state.itemsMap, item, merge);
  },

  CHANGE_ITEMS(state, { items, merge = true, view = DEFAULT_REQUEST_VIEW, addToList = true }: ChangeItemsPayload): void {
    const viewList = takeViewList(state, view);
    items.forEach(item => {
      if (addToList && !viewList.list.includes(item.id)) {
        viewList.list.push(item.id);
        viewList.count = (viewList.count || 0) + 1;
      }
      changeItem(state.itemsMap, item, merge);
    });
  },

  REMOVE(state, id: ID): void {
    Object.values(state.viewLists).forEach((viewList: any) => {
      if (viewList.count && viewList.list.includes(id)) {
        viewList.count -= 1;
      }
      viewList.list = viewList.list.filter(it => it !== id);
    });

    Vue.delete(state.itemsMap, id);
  },

  REMOVE_FROM_LIST(state, { id, view = DEFAULT_REQUEST_VIEW }: RemoveFromListPayload): void {
    const ids = castArray(id);
    const requestView = state.viewLists[view];
    if (requestView) {
      const removedCount = intersection(requestView.list, ids).length;
      requestView.list = requestView.list?.filter(it => !ids.includes(it));
      if (requestView.count && removedCount) {
        requestView.count -= removedCount;
      }
    }
  },

  CLEAR_LIST(state, view: string = DEFAULT_REQUEST_VIEW): void {
    state.viewLists[view].list = [];
    state.viewLists[view].count = 0;
  },

  SET_ERRORS(state, error: object): void {
    state.errors = error;
  },

  CLEAR_ERRORS(state): void {
    state.errors = {};
  },

  LOADING(state, view: string = DEFAULT_REQUEST_VIEW): void {
    if (state.viewLists[view]) {
      state.viewLists[view].loading = true;
    }
  },

  LOADED(state, view: string = DEFAULT_REQUEST_VIEW): void {
    if (state.viewLists[view]) {
      state.viewLists[view].loading = false;
    }
  },

  PROCESSING_CUSTOM_ACTION(state, id: ID): void {
    Vue.set(state.runningActions, id, true);
  },

  FINISHED_CUSTOM_ACTION(state, id: ID): void {
    if (state.runningActions?.[id]) {
      Vue.delete(state.runningActions, id);
    }
  },
  FLUSH_STATE(state): void {
    Object.assign(state, getInitialState());
  },
});

export { mergeObjects };
export default createMutations;
